import { getCurrentLocale } from '../i18n/format';
import type { Locale } from '../i18n/locale';
import ptKanban from '../i18n/locales/pt/kanban';
import enKanban from '../i18n/locales/en/kanban';
import esKanban from '../i18n/locales/es/kanban';
import { TASK_STATUS_OPTIONS, calculateTaskStatus } from './columnTypes';
import type { ColumnCategory, KanbanColumn } from '../types/database';

/**
 * A CATEGORIA DA COLUNA, e o que ela existe para impedir.
 *
 * Até 24/08/2026 a coluna do Kanban ERA o status: cinco ids fixos, e uma
 * escada de `if` espalhada por três arquivos decidindo o progresso a partir
 * desses ids. Liberar a coluna nesse desenho significava dado corrompido: a
 * agência criava "Publicado", arrastava o cartão para lá, e o Portfólio
 * continuava lendo 0% para sempre, porque nenhum `if` conhecia esse id.
 *
 * O conserto separa duas perguntas que tinham virado uma só:
 *   - ONDE o cartão está  -> a coluna, livre, do time (`tasks.status`)
 *   - QUANTO ele avançou   -> o progresso, canônico, da diretoria (`tasks.progress`)
 * A categoria é a ponte declarada entre as duas. É o mesmo mecanismo que o
 * Jira chama de status category e o Linear chama de state type, e os dois
 * chegaram nele pela mesma parede.
 *
 * REGRA DE OURO, e o motivo de ela estar escrita aqui e não num comentário
 * solto: a categoria só decide progresso em MOVIMENTO EXPLÍCITO do usuário
 * (arrastar o cartão, escolher a coluna no card, criar o cartão dentro dela).
 * Nunca em varredura, nunca em reconciliação em massa. Tarefa importada do
 * Trello com 100% de progresso numa coluna "A fazer" é um dado legítimo do
 * cliente, e uma rotina que "arrumasse" isso apagaria o único dado que o
 * importador conseguiu salvar.
 */

const KANBAN_COPY: Record<Locale, typeof ptKanban> = {
  'pt-BR': ptKanban,
  en: enKanban,
  es: esKanban,
};

const t = (): typeof ptKanban => KANBAN_COPY[getCurrentLocale()] ?? ptKanban;

/**
 * Ordem canônica. É a ordem em que as categorias aparecem no editor e a ordem
 * que uma coluna nova herda pela posição (ver `categoriaSugerida`).
 */
export const COLUMN_CATEGORY_ORDER: ColumnCategory[] = [
  'not_started',
  'in_progress',
  'on_hold',
  'completed',
];

/**
 * Cor da categoria: token, sempre (Lei 1). Não confundir com a cor da COLUNA,
 * que é escolha do usuário, vem do banco em hex e entra por `style`.
 */
const CATEGORY_TOKEN: Record<ColumnCategory, string> = {
  not_started: 'var(--color-text-muted)',
  in_progress: 'var(--color-info)',
  on_hold: 'var(--color-warning)',
  completed: 'var(--color-success)',
};

/**
 * Rótulo e ajuda de cada categoria, na língua corrente. Getters pelo mesmo
 * motivo de `columnTypes`: resolver no import congelaria o idioma da primeira
 * pintura.
 */
export const COLUMN_CATEGORIES = COLUMN_CATEGORY_ORDER.map((value) => ({
  value,
  color: CATEGORY_TOKEN[value],
  get label() { return t().categories[value]; },
  get hint() { return t().categories.hint[value]; },
}));

export const getCategoryInfo = (category: ColumnCategory) =>
  COLUMN_CATEGORIES.find((c) => c.value === category) ?? COLUMN_CATEGORIES[0];

/**
 * As cinco colunas que todo projeto ganha ao nascer.
 *
 * Os ids são as strings LITERAIS que `tasks.status` já guardava antes da
 * tabela `kanban_columns` existir. É o que permitiu semear o quadro sem migrar
 * uma linha de `tasks` sequer, e é por isso que o editor deixa RENOMEAR uma
 * coluna mas nunca trocar o id dela.
 *
 * `paused` e `blocked` caem em `on_hold`, a categoria que não mexe no
 * progresso: o comportamento do quadro padrão fica idêntico ao de antes.
 */
const CATEGORY_BY_SEED_ID: Record<string, ColumnCategory> = {
  not_started: 'not_started',
  in_progress: 'in_progress',
  completed: 'completed',
  paused: 'on_hold',
  blocked: 'on_hold',
};

export const DEFAULT_KANBAN_COLUMNS: KanbanColumn[] = TASK_STATUS_OPTIONS.map((opt, index) => ({
  id: opt.value,
  get name() { return opt.label; },
  color: opt.color,
  position: index,
  wipLimit: 0,
  category: CATEGORY_BY_SEED_ID[opt.value] ?? 'in_progress',
}));

/**
 * O progresso que a categoria manda gravar, ou `null` para "não mexe".
 *
 * Reproduz exatamente o que os três `if` espalhados faziam com os cinco ids
 * fixos, agora num lugar só:
 *   not_started -> 0            (zera)
 *   in_progress -> 5, só se estava em 0   (não rebaixa trabalho real)
 *   on_hold     -> null         (pausado e bloqueado nunca mexiam no progresso)
 *   completed   -> 100          (fecha)
 *
 * `null` é diferente de `0` de propósito: quem chama precisa saber a diferença
 * entre "grave zero" e "não escreva nada".
 */
export const progressForCategory = (
  category: ColumnCategory,
  currentProgress: number
): number | null => {
  switch (category) {
    case 'not_started':
      return 0;
    case 'completed':
      return 100;
    case 'in_progress':
      return (Number(currentProgress) || 0) === 0 ? 5 : null;
    case 'on_hold':
      return null;
  }
};

/** A coluna daquele id no quadro, ou `null`. Nunca chuta a primeira. */
export const findColumn = (
  statusId: string | null | undefined,
  columns: KanbanColumn[]
): KanbanColumn | null => {
  if (!statusId) return null;
  return columns.find((c) => c.id === statusId) ?? null;
};

/**
 * Nome e cor para exibir um `tasks.status` qualquer (barra do Gantt, célula da
 * tabela, chip de subtarefa, bloco do Calendário).
 *
 * O que ela existe para NÃO fazer: o `getStatusInfo` antigo devolvia o
 * PRIMEIRO status do enum quando não reconhecia o valor, então todo cartão em
 * "Publicado" aparecia como "Não iniciado", cinza, em quatro telas. Não
 * quebrava nada: só mentia, que é pior. Aqui, id desconhecido devolve o
 * próprio id como rótulo e uma cor neutra, e o usuário vê que há algo a
 * resolver em vez de ver um dado errado com cara de certo.
 */
export const resolveStatusInfo = (
  statusId: string | null | undefined,
  columns: KanbanColumn[]
): { id: string; label: string; color: string; category: ColumnCategory | null } => {
  const col = findColumn(statusId, columns);
  if (col) {
    return { id: col.id, label: col.name, color: col.color, category: col.category };
  }
  const fallback = DEFAULT_KANBAN_COLUMNS.find((c) => c.id === statusId);
  if (fallback) {
    return {
      id: fallback.id,
      label: fallback.name,
      color: fallback.color,
      category: fallback.category,
    };
  }
  return {
    id: statusId || '',
    label: statusId || '',
    color: 'var(--color-text-muted)',
    category: null,
  };
};

/**
 * Rótulo e cor de UMA TAREFA, resolvidos contra o quadro do projeto.
 *
 * É a fonte única de cor de status do produto. Antes desta função, a barra do
 * Gantt, o bloco do Calendário e a faixa do Cronograma tinham cada um a sua
 * cópia de `getStatusInfo(calculateTaskStatus(task)).color`, que descartava o
 * status persistido e recalculava tudo pelo progresso: a mesma tarefa saía
 * verde numa tela e azul na outra, e cartão em coluna livre nunca aparecia com
 * a cor da coluna dele.
 *
 * Tarefa sem status gravado cai na CATEGORIA derivada do progresso e pega a
 * primeira coluna daquela categoria no quadro, e não um id fixo: em quadro
 * customizado, "em andamento" pode se chamar "Redação".
 */
export const taskStatusInfo = (
  task: { progress?: number; status?: string | null },
  columns: KanbanColumn[]
): { id: string; label: string; color: string; category: ColumnCategory | null } => {
  if (task.status) return resolveStatusInfo(task.status, columns);

  const derivada = calculateTaskStatus(task) as ColumnCategory;
  const coluna = firstColumnOfCategory(columns, derivada);
  if (coluna) {
    return { id: coluna.id, label: coluna.name, color: coluna.color, category: coluna.category };
  }
  return resolveStatusInfo(derivada, columns);
};

/** A categoria de um status, resolvida contra o quadro. */
export const categoryOf = (
  statusId: string | null | undefined,
  columns: KanbanColumn[]
): ColumnCategory | null => resolveStatusInfo(statusId, columns).category;

/** A primeira coluna (por posição) de uma categoria. Usada por atalho e undo. */
export const firstColumnOfCategory = (
  columns: KanbanColumn[],
  category: ColumnCategory
): KanbanColumn | null =>
  [...columns].sort((a, b) => a.position - b.position).find((c) => c.category === category) ?? null;

/**
 * Todos os ids terminais do quadro.
 *
 * Lista, e não um id só, porque quadro real tem mais de uma saída: o Jira
 * entrega "Done" e "Won't Do" na mesma categoria, e a agência quer "Publicado"
 * e "Cancelado pelo cliente". Cycle time e throughput contam as duas.
 */
export const doneColumnIds = (columns: KanbanColumn[]): string[] =>
  columns.filter((c) => c.category === 'completed').map((c) => c.id);

/**
 * A categoria que uma coluna nova herda pela POSIÇÃO em que nasce.
 *
 * Vem direto do benchmark: para quem chega do Trello, criar coluna é digitar um
 * nome, e não aprender um conceito novo. A categoria vem pré-escolhida e fica
 * visível e editável na mesma caixa, em vez de ser uma pergunta a mais. O modo
 * de falha que isso evita está documentado na importação Trello para Jira, em
 * que toda coluna fora do padrão cai calada em "a fazer" e o usuário só
 * descobre pelo relatório errado.
 */
export const suggestedCategory = (position: number, total: number): ColumnCategory => {
  if (total <= 0 || position <= 0) return 'not_started';
  if (position >= total - 1) return 'completed';
  return 'in_progress';
};

/**
 * As invariantes do quadro, na hora de salvar o editor.
 *
 * Roubadas do Linear, que garante pelo menos um estado por categoria de borda.
 * Sem coluna de entrada não há onde nascer cartão; sem coluna de saída, cycle
 * time, throughput e o percentual do Portfólio ficam permanentemente em zero e
 * o quadro mente para o time inteiro.
 */
export const validateColumns = (
  columns: KanbanColumn[]
): { ok: true } | { ok: false; reason: 'needsName' | 'needsTodo' | 'needsDone' } => {
  if (columns.some((c) => !c.name.trim())) return { ok: false, reason: 'needsName' };
  if (!columns.some((c) => c.category === 'not_started')) return { ok: false, reason: 'needsTodo' };
  if (!columns.some((c) => c.category === 'completed')) return { ok: false, reason: 'needsDone' };
  return { ok: true };
};

/**
 * Id textual e estável para uma coluna nova.
 *
 * Textual, e não UUID, por uma razão concreta: duplicar projeto copia as linhas
 * de `kanban_columns` com o MESMO id, então `tasks.status` atravessa a cópia
 * sem nenhum remapeamento. Com UUID por projeto, cada duplicação exigiria um
 * mapa idAntigo -> idNovo e todo cartão cairia na primeira coluna no dia em que
 * alguém esquecesse de aplicá-lo.
 */
export const newColumnId = (existing: KanbanColumn[]): string => {
  const usados = new Set(existing.map((c) => c.id));
  let n = existing.length + 1;
  let id = `col_${n}`;
  while (usados.has(id)) {
    n += 1;
    id = `col_${n}`;
  }
  return id;
};
